/**
 * Branding Configuration (White-Label)
 * Centralized branding values read from environment variables.
 * Every consumer reads from here — never hardcode brand strings elsewhere.
 */

export interface BrandingColors {
  /** Primary color — buttons, links, active states (e.g. "#a11858") */
  primaryMain: string;
  primaryLight: string;
  primaryDark: string;
  /** Secondary color — accents, secondary buttons (e.g. "#0d9488") */
  secondaryMain: string;
  secondaryLight: string;
  secondaryDark: string;
  /** Accent/brand color — info badges, highlights (e.g. "#7c3aed") */
  accentMain: string;
  accentLight: string;
  accentDark: string;
  /** Header/AppBar background color (e.g. "#1E293B") */
  headerBg: string;
  /** Login gradient start color */
  loginGradientStart: string;
  /** Login gradient end color */
  loginGradientEnd: string;
  /** Fundo escuro do painel de marca da tela de login */
  loginNavy: string;
  /** Cor de destaque da tela de login — filete, botão, foco dos campos */
  loginAccent: string;
  /** Cor do texto SOBRE o destaque. O site escreve em tinta escura no teal, não em branco. */
  loginAccentContrast: string;
}

export interface BrandingConfig {
  /** Application name (e.g. "MEV-Med") */
  appName: string;
  /** Short description / tagline */
  appDescription: string;
  /** Copyright holder text (e.g. "MEV-Med") */
  appCopyright: string;
  /** Path to the light/white logo (used on dark backgrounds) */
  logoLight: string;
  /** Path to the color/dark logo (used on light backgrounds) */
  logoDark: string;
  /** Path to the small logo / icon */
  logoSmall: string;
  /** Path to the favicon */
  favicon: string;
  /** Foto de fundo do painel de marca da tela de login. Vazio = só o fundo escuro. */
  loginBackground: string;
  /** E-mail de suporte oferecido a quem não consegue entrar */
  supportEmail: string;
  /** Theme colors */
  colors: BrandingColors;
}

const branding: BrandingConfig = {
  appName: import.meta.env.VITE_APP_NAME || 'Lidera SST',
  appDescription: import.meta.env.VITE_APP_DESCRIPTION || 'Medicina Ocupacional',
  appCopyright: import.meta.env.VITE_APP_COPYRIGHT || 'Lidera SST',
  // Os padrões apontam para arquivos que EXISTEM no repositório. Os antigos
  // (`/images/logoMevMed*.png`) só existiam no servidor, então qualquer ambiente sem as
  // variáveis definidas — o desenvolvimento local, por exemplo — caía no texto de reserva do
  // BrandLogo e a marca sumia da tela.
  logoLight: import.meta.env.VITE_LOGO_LIGHT || '/logo/LogoLiderasst.png',
  logoDark: import.meta.env.VITE_LOGO_DARK || '/logo/LogoLiderasstEscuro.webp',
  logoSmall: import.meta.env.VITE_LOGO_SMALL || '/logo/LogoLiderasst.png',
  favicon: import.meta.env.VITE_FAVICON || '/favicon.png',
  loginBackground: import.meta.env.VITE_LOGIN_BACKGROUND ?? '/images/login-background.webp',
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || 'suporte@liderasst.com.br',
  colors: {
    primaryMain: import.meta.env.VITE_THEME_PRIMARY || '#a11858',
    primaryLight: import.meta.env.VITE_THEME_PRIMARY_LIGHT || '#c24e7e',
    primaryDark: import.meta.env.VITE_THEME_PRIMARY_DARK || '#7a1042',
    secondaryMain: import.meta.env.VITE_THEME_SECONDARY || '#0d9488',
    secondaryLight: import.meta.env.VITE_THEME_SECONDARY_LIGHT || '#2dd4bf',
    secondaryDark: import.meta.env.VITE_THEME_SECONDARY_DARK || '#0f766e',
    accentMain: import.meta.env.VITE_THEME_ACCENT || '#7c3aed',
    accentLight: import.meta.env.VITE_THEME_ACCENT_LIGHT || '#a78bfa',
    accentDark: import.meta.env.VITE_THEME_ACCENT_DARK || '#5b21b6',
    headerBg: import.meta.env.VITE_THEME_HEADER_BG || '#1E293B',
    loginGradientStart: import.meta.env.VITE_THEME_LOGIN_GRADIENT_START || '#7a1042',
    loginGradientEnd: import.meta.env.VITE_THEME_LOGIN_GRADIENT_END || '#1E293B',
    // Os três valores vêm de liderasst.com.br (variáveis --navy, --teal e a cor do
    // .primary-button do site), para a entrada do sistema falar a mesma língua do site.
    loginNavy: import.meta.env.VITE_THEME_LOGIN_NAVY || '#0a1c3d',
    loginAccent: import.meta.env.VITE_THEME_LOGIN_ACCENT || '#00bab4',
    loginAccentContrast: import.meta.env.VITE_THEME_LOGIN_ACCENT_CONTRAST || '#062a32',
  },
};

export default branding;
